(() => {
  const root = document.querySelector('[data-belantara-tap]');
  if (!root) return;

  const enabled = root.dataset.marketingConsentEnabled === 'true';
  if (!enabled) return;

  const publicApiKey = root.dataset.klaviyoPublicApiKey || '';
  const listId = root.dataset.marketingListId || '';
  const consentLabel = root.dataset.marketingConsentLabel || 'Keep me close to Belantara';
  const consentDescription = root.dataset.marketingConsentDescription || 'New releases, stories and occasional notes. Unsubscribe anytime.';
  const confirmedLabel = root.dataset.marketingConsentConfirmedLabel || 'You’re already subscribed.';
  const shopifyConsentConfirmed = root.dataset.customerAcceptsMarketing === 'true';
  const forms = [...root.querySelectorAll('[data-tap-form], [data-tap-receipt-form]')];

  const normalizeEmail = value => (value || '').trim().toLowerCase();

  function signedInEmail() {
    return normalizeEmail(root.dataset.customerEmail);
  }

  function rememberedEmail() {
    try {
      return normalizeEmail(localStorage.getItem('belantara_tap_email'));
    } catch (_) {
      return '';
    }
  }

  function knownEmail() {
    return signedInEmail() || rememberedEmail();
  }

  function consentEmail() {
    try {
      return normalizeEmail(localStorage.getItem('belantara_tap_marketing_consent_email'));
    } catch (_) {
      return '';
    }
  }

  function hasLocalConsent(email) {
    const normalized = normalizeEmail(email);
    return Boolean(normalized && consentEmail() === normalized);
  }

  function rememberConsent(email) {
    try {
      localStorage.setItem('belantara_tap_marketing_consent_email', normalizeEmail(email));
      localStorage.removeItem('belantara_tap_marketing_consent');
    } catch (_) {}
  }

  function hasShopifyConsent(email) {
    const normalized = normalizeEmail(email);
    return Boolean(
      shopifyConsentConfirmed &&
      normalized &&
      normalized === signedInEmail()
    );
  }

  function hasConfirmedConsent(email) {
    return hasShopifyConsent(email) || hasLocalConsent(email);
  }

  function createConsentControl(form, index) {
    if (form.querySelector('[data-tap-marketing-consent]')) return;

    const emailSlot = form.querySelector('.tap-email-slot');
    if (!emailSlot) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'tap-consent';
    wrapper.dataset.tapMarketingConsent = '';
    wrapper.hidden = true;

    const label = document.createElement('label');
    label.className = 'tap-consent__label';

    const checkbox = document.createElement('input');
    checkbox.className = 'tap-consent__checkbox';
    checkbox.type = 'checkbox';
    checkbox.name = 'marketing_consent';
    checkbox.value = 'yes';
    checkbox.id = `tap-marketing-consent-${index}`;

    const copy = document.createElement('span');
    copy.className = 'tap-consent__copy';

    const title = document.createElement('span');
    title.className = 'tap-consent__title';
    title.textContent = consentLabel;

    const description = document.createElement('span');
    description.className = 'tap-consent__description';
    description.textContent = consentDescription;

    copy.append(title, description);
    label.append(checkbox, copy);
    wrapper.append(label);
    emailSlot.insertAdjacentElement('afterend', wrapper);

    const status = document.createElement('div');
    status.className = 'tap-consent__description';
    status.dataset.tapMarketingConsentStatus = '';
    status.textContent = confirmedLabel;
    status.hidden = true;
    wrapper.insertAdjacentElement('afterend', status);
  }

  function currentEmail(form) {
    const input = form.querySelector('input[name="email"]');
    return normalizeEmail(input ? input.value : knownEmail());
  }

  async function recognizedFor(form) {
    const input = form.querySelector('input[name="email"]');
    const email = currentEmail(form);
    const recognizedEmail = normalizeEmail(knownEmail());

    if (input) {
      return Boolean(email && email === recognizedEmail);
    }

    if (window.BelantaraIdentity?.isKnown) {
      try {
        return Boolean(
          await window.BelantaraIdentity.isKnown(
            root.dataset.customerEmail
          )
        );
      } catch (_) {}
    }

    return Boolean(recognizedEmail);
  }

  async function refreshConsent() {
    await Promise.all(forms.map(async form => {
      const wrapper = form.querySelector('[data-tap-marketing-consent]');
      const consentStatus = form.querySelector('[data-tap-marketing-consent-status]');
      const recognitionStatus = form.querySelector('[data-tap-recognition-status]');
      if (!wrapper) return;

      const email = currentEmail(form);
      const recognized = await recognizedFor(form);
      const confirmedConsent = hasConfirmedConsent(email);
      const suppressAsk = recognized || confirmedConsent;

      wrapper.hidden = suppressAsk;

      if (consentStatus) {
        consentStatus.hidden = !confirmedConsent;
      }

      if (recognitionStatus) {
        recognitionStatus.hidden = !recognized || confirmedConsent;
      }
    }));
  }

  async function subscribe(email) {
    if (!publicApiKey || !listId) {
      throw new Error('Missing Klaviyo marketing consent configuration.');
    }

    const params = new URLSearchParams(location.search);
    const properties = {
      marketing_consent_source: 'belantara_tap',
      marketing_consent_context: params.get('src') || '',
      marketing_consent_event: params.get('event') || '',
      marketing_consent_venue: params.get('venue') || '',
      marketing_consent_touchpoint: params.get('touchpoint') || '',
      marketing_consent_intent: params.get('tap_intent') || params.get('intent') || '',
      marketing_consent_topic: params.get('topic') || '',
      marketing_consent_tag: params.get('tag') || '',
      marketing_consent_method: params.get('method') || 'nfc'
    };

    const cleanProperties = Object.fromEntries(
      Object.entries(properties).filter(([, value]) => value !== '' && value != null)
    );

    const response = await fetch(
      `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicApiKey)}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/vnd.api+json',
          revision: '2026-07-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Belantara Tap',
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email,
                    properties: cleanProperties,
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: 'SUBSCRIBED'
                        }
                      }
                    }
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId
                }
              }
            }
          }
        })
      }
    );

    if (!response.ok) {
      throw new Error(`Klaviyo subscription failed with ${response.status}.`);
    }
  }

  window.BelantaraTapMarketingConsent = Object.freeze({
    subscribe,
    rememberConsent,
    hasLocalConsent,
    hasConfirmedConsent
  });

  forms.forEach(createConsentControl);
  void refreshConsent();

  root.addEventListener('click', () => queueMicrotask(() => void refreshConsent()));

  root.addEventListener('input', event => {
    if (event.target.matches('input[name="email"]')) void refreshConsent();
  });

  root.addEventListener(
    'submit',
    event => {
      const form = event.target.closest('[data-tap-form]');
      if (!form || form.dataset.marketingConsentReady === 'true') return;

      const checkbox = form.querySelector('input[name="marketing_consent"]');
      if (!checkbox?.checked) return;

      const email = currentEmail(form);
      const input = form.querySelector('input[name="email"]');
      if (input && !input.checkValidity()) return;
      if (!email) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const button = form.querySelector('button[type="submit"]');
      const error = form.querySelector('[data-tap-error]');
      const original = button?.textContent || '';

      if (button) {
        button.disabled = true;
        button.textContent = root.dataset.savingLabel || 'Saving…';
      }
      if (error) error.textContent = '';

      subscribe(email)
        .then(() => {
          rememberConsent(email);
          form.dataset.marketingConsentReady = 'true';
          if (button) {
            button.disabled = false;
            button.textContent = original;
          }
          void refreshConsent();
          form.requestSubmit();
        })
        .catch(() => {
          if (button) {
            button.disabled = false;
            button.textContent = original;
          }
          if (error) {
            error.textContent = root.dataset.genericError || 'Something went wrong. Please try again.';
          }
        });
    },
    true
  );
})();
